/* Base Layout
Author: Maxwell Levene @maxlevene920- Ellmarr
Date: 04/30/2025
Description: Service for managing carbon credit transactions. Handles CRUD operations
for transactions including payment status updates and user transaction history.
*/

import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface Transaction {
  id: string;
  created_at: string;
  updated_at: string;
  user_id: string;
  credits: number;
  value: number; // Changed from 'pounds' to 'value' to match DB
  status: string; // Changed from boolean 'paid' to string 'status'
  user?: {
    username: string;
  };
}

export interface TransactionUpdate {
  status?: "paid" | "pending";
  credits?: number;
  value?: number;
}

export const transactionsService = {
  // Get all transactions with pagination
  getTransactions: async (page: number = 1, pageSize: number = 25): Promise<{ transactions: Transaction[]; total: number }> => {
    try {
      console.log(`Fetching transactions: page ${page}, size ${pageSize}`);
      
      // Calculate range for pagination
      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;
      
      // Fetch transactions without join first
      const { data: transactionsData, error: transactionsError, count } = await supabase
        .from('carbon_credits_transactions')
        .select('*', { count: 'exact' })
        .order('created_at', { ascending: false })
        .range(from, to);
      
      if (transactionsError) {
        console.error("Error fetching transactions:", transactionsError);
        throw transactionsError;
      }
      
      // Get unique user IDs from transactions
      const userIds = [...new Set(transactionsData?.map(t => t.user_id).filter(Boolean) || [])];
      
      // Fetch user data separately
      let usersData: any[] = [];
      if (userIds.length > 0) {
        const { data: userData, error: usersError } = await supabase
          .from('users')
          .select('id, username')
          .in('id', userIds);
        
        if (usersError) {
          console.error("Error fetching users:", usersError);
          // Continue without user data rather than failing
        } else {
          usersData = userData || [];
        }
      }
      
      // Create a map for quick user lookup
      const usersMap = new Map(usersData.map(user => [user.id, user]));
      
      // Combine transaction and user data
      const transactions = (transactionsData || []).map((row: any) => ({
        id: row.id,
        created_at: row.created_at,
        updated_at: row.updated_at,
        user_id: row.user_id,
        credits: row.credits,
        value: row.value,
        status: row.status,
        user: usersMap.get(row.user_id) || undefined
      }));
      
      const total = count || 0;
      
      console.log(`Successfully fetched ${transactions.length} transactions`);
      return { transactions, total };
    } catch (error: any) {
      console.error("Error in getTransactions:", error);
      toast.error("Failed to fetch transactions");
      throw error;
    }
  },

  // Update a transaction
  updateTransaction: async (id: string, updates: TransactionUpdate): Promise<Transaction> => {
    try {
      console.log(`Updating transaction ${id}:`, updates);
      
      const { data, error } = await supabase
        .from('carbon_credits_transactions')
        .update(updates)
        .eq('id', id)
        .select('*')
        .single();
      
      if (error) {
        console.error("Error updating transaction:", error);
        throw error;
      }
      
      if (!data) {
        throw new Error("Failed to update transaction. No data returned.");
      }
      
      // Fetch user data separately for the updated transaction
      let userData = null;
      if (data.user_id) {
        const { data: userResult, error: userError } = await supabase
          .from('users')
          .select('id, username')
          .eq('id', data.user_id)
          .single();
        
        if (!userError && userResult) {
          userData = userResult;
        }
      }
      
      const transaction = {
        id: data.id,
        created_at: data.created_at,
        updated_at: data.updated_at,
        user_id: data.user_id,
        credits: data.credits,
        value: data.value,
        status: data.status,
        user: userData || undefined
      };
      
      console.log("Successfully updated transaction:", transaction);
      toast.success("Transaction updated successfully");
      return transaction;
    } catch (error: any) {
      console.error("Error in updateTransaction:", error);
      toast.error("Failed to update transaction");
      throw error;
    }
  },

  // Get a single transaction by ID
  getTransaction: async (id: string): Promise<Transaction> => {
    try {
      console.log(`Fetching transaction ${id}`);
      
      const { data, error } = await supabase
        .from('carbon_credits_transactions')
        .select('*')
        .eq('id', id)
        .single();
      
      if (error) {
        console.error("Error fetching transaction:", error);
        throw error;
      }
      
      if (!data) {
        throw new Error(`Transaction with ID ${id} not found`);
      }
      
      // Fetch user data separately
      let userData = null;
      if (data.user_id) {
        const { data: userResult, error: userError } = await supabase
          .from('users')
          .select('id, username')
          .eq('id', data.user_id)
          .single();
        
        if (!userError && userResult) {
          userData = userResult;
        }
      }
      
      const transaction = {
        id: data.id,
        created_at: data.created_at,
        updated_at: data.updated_at,
        user_id: data.user_id,
        credits: data.credits,
        value: data.value,
        status: data.status,
        user: userData || undefined
      };
      
      console.log("Successfully fetched transaction:", transaction);
      return transaction;
    } catch (error: any) {
      console.error("Error in getTransaction:", error);
      toast.error("Failed to fetch transaction details");
      throw error;
    }
  },

  // Get transactions for a specific user
  getUserTransactions: async (userId: string): Promise<Transaction[]> => {
    try {
      console.log(`Fetching transactions for user ${userId}`);
      
      const { data: transactionsData, error } = await supabase
        .from('carbon_credits_transactions')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });
      
      if (error) {
        console.error("Error fetching user transactions:", error);
        throw error;
      }
      
      // Fetch user data separately
      let userData = null;
      if (userId) {
        const { data: userResult, error: userError } = await supabase
          .from('users')
          .select('id, username')
          .eq('id', userId)
          .single();
        
        if (!userError && userResult) {
          userData = userResult;
        }
      }
      
      const transactions = (transactionsData || []).map((row: any) => ({
        id: row.id,
        created_at: row.created_at,
        updated_at: row.updated_at,
        user_id: row.user_id,
        credits: row.credits,
        value: row.value,
        status: row.status,
        user: userData || undefined
      }));
      
      console.log(`Successfully fetched ${transactions.length} transactions for user`);
      return transactions;
    } catch (error: any) {
      console.error("Error in getUserTransactions:", error);
      toast.error("Failed to fetch user transactions");
      throw error;
    }
  },
}; 